import { Button, Divider, Modal, Row, Select, message } from "antd"
import dayjs from "dayjs"
import React, { useEffect, useMemo, useState } from "react"
import styled from "styled-components"

import {
    deleteBookingAsAdmin,
    unlockSlotAsAdmin,
} from "../../../../../../../api"
import AppSelect from "../../../../../../_shared/components/FormItems/AppSelect"
import { useStateValue } from "../../../../../../_shared/context/AppStateStore"
import { uniqueFilter } from "../../../../../../_shared/toolbox"
import Slot from "./Slot"
import { pivotSort, getSlotStartEnd, isSameOrBefore } from "../util"

// ----------------------------------------------------------------------------

function _SlotForm(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { values, onValueChange, eventTypes, setFormState } = props
    const { toManuallyReconnect = [] } = values ?? {}

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const [dateFilter, setDateFilter] = useState("all")
    const [locationFilter, setLocationFilter] = useState("all")
    const [filteredSlots, setFilteredSlots] = useState([])
    const [{ token }] = useStateValue()

    // -------------------------------------
    // Effects
    // -------------------------------------

    const typeMap = useMemo(() => {
        if (!eventTypes) {
            return {}
        }

        return eventTypes.reduce((acc, current) => {
            return {
                ...acc,
                [current.id]: current,
            }
        }, {})
    }, [eventTypes])

    useEffect(() => {
        let filtered =
            values?.slots?.map((slot, originalIndex) => ({
                ...slot,
                originalIndex,
            })) ?? []
        if (dateFilter !== "all") {
            filtered = filtered.filter((slot) => slot.date === dateFilter)
        }
        if (locationFilter !== "all") {
            filtered = filtered.filter(
                (slot) => slot.locationCode === locationFilter
            )
        }
        setFilteredSlots(filtered)
        //eslint-disable-next-line
    }, [locationFilter, dateFilter, values?.slots])

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function renderDateSelectOptions() {
        return getUniqueDates()?.map((date) => (
            <Select.Option key={date} value={date}>
                {dayjs(date).format("DD MMMM YYYY")}
            </Select.Option>
        ))
    }
    function renderLocationSelectOptions() {
        return values.locations?.map((l) => (
            <Select.Option key={l.code} value={l.code}>
                {l.address}, {l.city} ({l.province})
            </Select.Option>
        ))
    }

    function forceUnlock(slot) {
        return () => {
            async function execute() {
                try {
                    const { id } = slot
                    await unlockSlotAsAdmin(id, token)
                    const handler = onValueChange("slots")
                    const newSlots = values?.slots?.map((s) => {
                        if (s.id === slot.id) {
                            return {
                                ...s,
                                lockedStatus: {
                                    locked: false,
                                    lockedBy: null,
                                    lockedUntil: null,
                                },
                            }
                        } else {
                            return s
                        }
                    })
                    handler(newSlots)
                    message.success("Slot sbloccato.")
                } catch (e) {
                    console.error(e)
                    message.error("Operazione non riuscita.")
                }
            }
            Modal.confirm({
                onOk: execute,
                content: [
                    "Sei sicuro di voler sbloccare questo slot? ",
                    "Questa azione non è reversibile.",
                ].join(""),
                title: "Conferma operazione",
            })
        }
    }

    function deleteSlotBooking(booker, slot) {
        return () => {
            async function execute() {
                try {
                    const { id: bookingId } = booker
                    await deleteBookingAsAdmin(bookingId, token)
                    const handler = onValueChange("slots")
                    const newSlots = values?.slots?.map((s) => {
                        if (s.id === slot.id) {
                            return { ...s, booker: null }
                        } else {
                            return s
                        }
                    })
                    handler(newSlots)
                    message.success("Prenotazione eliminata.")
                } catch (e) {
                    // eslint-disable-next-line
                    console.log(e)
                    message.error(
                        "Non è stato possibile eliminare la prenotazione."
                    )
                }
            }
            Modal.confirm({
                onOk: execute,
                content: [
                    "Sei sicuro di voler eliminare questa prenotazione? ",
                    "Questa azione risulterà nell'eliminazione permanente ",
                    "della prenotazione anche qualora non si ",
                    "salvino le modifiche all'evento.",
                ].join(""),
                title: "Conferma operazione",
            })
        }
    }

    function getUniqueDates() {
        return values?.slots?.map((slots) => slots?.date).filter(uniqueFilter)
    }

    function updateSlot(index) {
        return (field, value) => {
            const newSlots = [...values.slots]
            const key = newSlots[index].id || newSlots[index].tempId
            newSlots[index][field] = value
            if (field === "eventType") {
                onValueChange("slots", {
                    recomputeSlots: true,
                    changedField: "slotEventType",
                    slotIndex: index,
                    extraPivots: [key],
                })(newSlots)
            } else {
                onValueChange("slots")(newSlots)
            }
        }
    }

    function getApplicableSecondaryTypes(slot, pivots) {
        const slotKey = slot.id || slot.tempId
        const allTypes = [...(eventTypes?.map((et) => et.id) ?? [])]
        pivots = pivots.filter(
            (pivot) =>
                pivot.date === slot.date &&
                pivot.locationCode === slot.locationCode
        )
        pivots.sort(pivotSort)
        const [slotStart, slotEnd] = getSlotStartEnd(slot)
        const nextPivot = pivots.find((pivot) => {
            const [pivotStart] = getSlotStartEnd(pivot)
            const pivotKey = pivot.id || pivot.tempId
            return slotKey !== pivotKey && isSameOrBefore(slotEnd, pivotStart)
        })

        if (!nextPivot) {
            return allTypes
        }

        const [nextStart] = getSlotStartEnd(nextPivot)

        return allTypes
            .map((type) => typeMap[type])
            .filter((et) => {
                const updatedEnd = dayjs(slotStart).add(
                    et.slotDuration,
                    "minutes"
                )
                return isSameOrBefore(updatedEnd, nextStart)
            })
            .map((et) => et.id)
    }

    function renderSlots() {
        const mainType = values?.eventType?.id
            ? values.eventType.id
            : values.eventType
        const pivots = values.slots.filter(
            (slot) => slot.eventType !== mainType || slot.booker
        )
        return filteredSlots?.map((slot) => {
            const location = values?.locations?.find(
                (l) => l.code === slot.locationCode
            )
            if (!location) {
                return null
            }

            return (
                <Slot
                    className="slot"
                    key={slot.id || slot.tempId}
                    slot={slot}
                    location={location}
                    agentReservationCodes={values?.agentReservationCodes}
                    onChange={updateSlot(slot.originalIndex)}
                    onDeleteBooking={deleteSlotBooking(slot.booker, slot)}
                    onForceUnlock={forceUnlock(slot)}
                    typeMap={typeMap}
                    mainEventType={mainType}
                    availableTypes={getApplicableSecondaryTypes(slot, pivots)}
                ></Slot>
            )
        })
    }

    function connectSlot(oldSlot, newSlotId) {
        const { booker, visible, agentReservation, note } = oldSlot
        const newSlotIndex = values.slots.findIndex(
            (s) => s.id === newSlotId || s.tempId === newSlotId
        )
        const newSlots = [...values.slots]
        newSlots[newSlotIndex] = {
            ...newSlots[newSlotIndex],
            booker,
            visible,
            agentReservation,
            note,
        }
        setFormState({
            ...values,
            slots: newSlots,
            toManuallyReconnect: values.toManuallyReconnect.filter(
                (s) => s !== oldSlot
            ),
        })
    }

    function getRecommendedConnection(oldSlot) {
        const relevantSlots = values.slots.filter(
            (s) =>
                oldSlot.date === s.date &&
                oldSlot.locationCode === s.locationCode &&
                oldSlot.eventType === s.eventType &&
                !s.note &&
                !s.booker &&
                !s.agentReservation
        )
        relevantSlots.push(oldSlot)
        relevantSlots.sort(pivotSort)
        const index = relevantSlots.findIndex((s) => s === oldSlot)
        return [
            relevantSlots[index - 2],
            relevantSlots[index - 1],
            relevantSlots[index + 1],
            relevantSlots[index + 2],
        ].filter(Boolean)
    }

    function renderManualReconnect() {
        if (!toManuallyReconnect?.length) {
            return
        }
        return (
            <div>
                <p style={{ fontWeight: "bold", maxWidth: 600 }}>
                    Attenzione! Dopo il ricalcolo, ci sono slot per i quali non
                    è stato possibile trovare un corrispettivo dopo la
                    generazione. Ulteriori modifiche agli slot esistenti
                    comporteranno la perdita definitiva dei dati.
                </p>
                <div>
                    {toManuallyReconnect.map((slot) => {
                        const id = slot.id || slot.tempId
                        const location = values?.locations?.find(
                            (l) => l.code === slot.locationCode
                        )
                        const recommended = getRecommendedConnection(slot)
                        return (
                            <div
                                key={id}
                                style={{
                                    marginBottom: 24,
                                    display: "flex",
                                }}
                            >
                                <div style={{ width: 400 }}>
                                    <Slot
                                        slot={slot}
                                        location={location}
                                        mainEventType={values.eventType}
                                        agentReservationCodes={
                                            values?.agentReservationCodes
                                        }
                                    />
                                </div>
                                <div style={{ marginLeft: 24 }}>
                                    <p>
                                        Seleziona una nuova fascia oraria per
                                        questo slot:
                                    </p>
                                    <Row>
                                        {recommended.map((recommendedSlot) => (
                                            <Button
                                                style={{ marginRight: 12 }}
                                                onClick={() =>
                                                    connectSlot(
                                                        slot,
                                                        recommendedSlot.id ||
                                                            recommendedSlot.tempId
                                                    )
                                                }
                                                key={
                                                    recommendedSlot.id ||
                                                    recommendedSlot.tempId
                                                }
                                            >
                                                {recommendedSlot.window}
                                            </Button>
                                        ))}
                                    </Row>
                                </div>
                            </div>
                        )
                    })}
                    <Divider />
                </div>
            </div>
        )
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------
    // -------------------------------------

    return (
        <div className={props.className}>
            {renderManualReconnect()}
            <div className="filters">
                <AppSelect value={dateFilter} onChange={setDateFilter}>
                    <Select.Option value="all">Tutte le date</Select.Option>
                    {renderDateSelectOptions()}
                </AppSelect>
                <AppSelect value={locationFilter} onChange={setLocationFilter}>
                    <Select.Option value="all">Tutte le location</Select.Option>
                    {renderLocationSelectOptions()}
                </AppSelect>
            </div>
            <div className="slots">{renderSlots()}</div>
            {/* {renderAddSlot()} */}
        </div>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

_SlotForm.defaultProps = {
    reloadEvent: () => {},
}

// ----------------------------------------------------------------------------

const SlotForm = styled(_SlotForm)`
    & {
        width: 100%;
        .filters {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 20px;
        }
        .slots {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 15px;
        }
    }
`
// ----------------------------------------------------------------------------

export default SlotForm
